/**
 * WoWyrd - Main Application Component
 *
 * Entry point routes directly to the Risk Fraud Prevention dashboard.
 */

import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';
import { useT } from './i18n';
import type { Locale } from './i18n';
import { LicenseBanner } from './components/risk/LicenseBanner';
import { DemoProvenanceBanner } from './components/risk/DemoProvenanceBanner';
import { isDemoMode } from './utils/formatting';

// Lazy load Risk dashboard (primary product)
const RiskDashboard = lazy(() => import('./pages/riskDashboard'));

// Configure React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000, // 1 second
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 3,
    },
  },
});

/**
 * Loading component for lazy-loaded routes
 */
const PageLoader: React.FC = () => {
  const { t } = useT();
  return (
    <div className="min-h-screen bg-wyrd-bg flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-wyrd-ember border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-wyrd-ink-dim">{t('shell.loading')}</p>
      </div>
    </div>
  );
};

/**
 * EN/UA language toggle — specs/product-kernel/dashboard-ukrainian-localization.md.
 * Persists to localStorage via useLocaleStore; a build-time VITE_DEFAULT_LOCALE picks the
 * first-load default (e.g. demo.wowyrd.net defaults to 'uk'), always user-switchable either
 * way.
 */
const LanguageToggle: React.FC = () => {
  const { locale, setLocale, t } = useT();
  const otherLocale: Locale = locale === 'en' ? 'uk' : 'en';
  return (
    <button
      onClick={() => setLocale(otherLocale)}
      aria-label={t('shell.langToggleLabel')}
      aria-pressed={locale === 'uk'}
      className="flex items-center gap-1 text-sm text-wyrd-ink-dim hover:text-wyrd-ink transition-colors"
    >
      <span className={locale === 'en' ? 'text-wyrd-ink font-medium' : ''}>EN</span>
      <span className="text-wyrd-hover">/</span>
      <span className={locale === 'uk' ? 'text-wyrd-ink font-medium' : ''}>UA</span>
    </button>
  );
};

/**
 * Shared app shell — header + main content area.
 * Used by both /dashboard and /risk/* routes.
 */
const AppShell: React.FC<{ children: React.ReactNode; subtitle?: string }> = ({
  children,
  subtitle,
}) => {
  const { t } = useT();
  return (
    <div className="min-h-screen bg-wyrd-bg text-wyrd-ink">
      <header className="bg-wyrd-raised border-b border-wyrd-line px-6 py-4">
        {/* flex-wrap: specs/simulation-lab/demo-neobank-traffic.md -- at 375px width, the title
            and the nav row (DemoProvenanceBanner/LicenseBanner/LanguageToggle/live indicator)
            had no wrap behavior and overlapped instead of stacking. */}
        <div className="flex items-center justify-between flex-wrap gap-y-2">
          <Link
            to="/"
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <img src="/wowyrd-logo.svg" alt="WoWyrd Logo" className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-semibold font-serif text-wyrd-ink">WoWyrd</h1>
              <p className="text-sm text-wyrd-ink-dim">{subtitle ?? t('shell.subtitleDefault')}</p>
            </div>
          </Link>
          <nav className="flex items-center gap-4 flex-wrap">
            <DemoProvenanceBanner />
            {/* Hidden in demo mode — the public demo has never had a real license file
                configured (it was never meant to), so LicenseBanner's "No license file found"
                state is expected, not a fault; showing a red error banner on a public demo
                reads as broken rather than informative. A real client install always sees it. */}
            {!isDemoMode() && <LicenseBanner />}
            <Link
              to="/risk"
              className="text-sm text-wyrd-ink-dim hover:text-wyrd-ink transition-colors"
            >
              {t('shell.navRisk')}
            </Link>
            <LanguageToggle />
            <div className="flex items-center space-x-2">
              {/* Semantic status color (connected = green), not brand chrome — matches
                  RiskHeader's own wsConnected indicator in riskDashboard.tsx. */}
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-wyrd-ink-dim">{t('shell.live')}</span>
            </div>
          </nav>
        </div>
      </header>
      <main className="p-6">{children}</main>
    </div>
  );
};

/**
 * Risk layout wrapper — wraps the Risk dashboard in AppShell.
 */
const RiskLayout: React.FC = () => {
  const { t } = useT();
  return (
    <AppShell subtitle={t('header.title')}>
      <Suspense fallback={<PageLoader />}>
        <RiskDashboard />
      </Suspense>
    </AppShell>
  );
};

/**
 * Main Application Component
 */
export const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/risk/*" element={<RiskLayout />} />
          <Route path="*" element={<Navigate to="/risk/feed" replace />} />
        </Routes>
      </Suspense>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#121317', // wyrd-raised
            color: '#ece8df', // wyrd-ink
            border: '1px solid #232429', // wyrd-line
          },
        }}
      />

      {/* React Query DevTools (development only) */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default App;
